import { IconButton, LinearProgress, Tooltip, Typography } from '@mui/joy';
import { ArrowClockwise, X } from '@phosphor-icons/react';
import clsx from 'clsx';
import { FormattedMessage, useIntl } from 'react-intl';

import imageIcon from '../../../../../../assets/svg/imageIcon.svg';
import otherFileTypeIcon from '../../../../../../assets/svg/otherFileType.svg';
import pdfIcon from '../../../../../../assets/svg/report/messenger/messagePDF.svg';
import videoIcon from '../../../../../../assets/svg/report/messenger/messageVideo.svg';
import docIcon from '../../../../../../assets/svg/report/messenger/messageWord.svg';
import {
  DOC_FILE_TYPES,
  IMAGE_FILE_TYPES,
  PDF_FILE_TYPE,
  VIDEO_FILE_TYPES,
} from '../../../../../../constants/fileTypeConstants';
import {
  DOC_MIME_TYPE,
  IMAGE_MIME_TYPES,
  PDF_MIME_TYPE,
  VIDEO_MIME_TYPES,
} from '../../../../../../constants/mimeTypeConstants';
import { PendingFilesObject } from '../messages';

import { Button } from '~/components/ui/button';
import { Card } from '~/components/ui/card';
import { Text } from '~/components/ui/text';

interface PendingAttachmentsProps {
  pendingFiles: PendingFilesObject[];
  fullWidth?: boolean;
  removePendingFile: (key: number) => void;
  retryUpload: () => void;
  error: {
    isError: boolean;
    message: string;
  };
}

const PendingAttachments = ({
  pendingFiles,
  removePendingFile,
  fullWidth,
  retryUpload,
  error,
}: PendingAttachmentsProps) => {
  const getIcon = (currfileType: string) => {
    if (IMAGE_FILE_TYPES.includes(currfileType)) return imageIcon;
    if (IMAGE_MIME_TYPES.includes(currfileType)) return imageIcon;
    if (VIDEO_FILE_TYPES.includes(currfileType)) return videoIcon;
    if (VIDEO_MIME_TYPES.includes(currfileType)) return videoIcon;
    if (DOC_FILE_TYPES.includes(currfileType)) return docIcon;
    if (DOC_MIME_TYPE === currfileType) return docIcon;
    if (PDF_FILE_TYPE === currfileType) return pdfIcon;
    if (PDF_MIME_TYPE === currfileType) return pdfIcon;

    return otherFileTypeIcon;
  };

  const intl = useIntl();

  return (
    <div className='flex flex-col gap-1'>
      {error.isError && (
        <Text level='body-md' className='text-brand-danger-600'>
          {error.message}
        </Text>
      )}
      <div
        className={clsx('flex flex-row gap-2 m-2 overflow-x-auto ', {
          'max-w-full': fullWidth,
          'max-w-[60vw]': !fullWidth,
        })}
        style={{
          scrollbarWidth: 'thin',
        }}
      >
        {pendingFiles.map(({ file, status }, key) => (
          <Card
            size='sm'
            key={key}
            orientation='horizontal'
            className={clsx('items-center w-56', {
              'opacity-55': status === 'loading',
              'opacity-100': status !== 'loading',
            })}
          >
            <div className='w-10 h-10 rounded shrink-0 bg-zinc-100'>
              <img
                className='w-full h-full'
                src={getIcon(file.type)}
                alt={file.name}
              />
            </div>
            {status === 'failed' ? (
              <div className='flex-1 min-w-0'>
                <Typography level='title-sm' className='truncate'>
                  <FormattedMessage defaultMessage='Failed to upload file' />
                </Typography>
                <Button
                  size='sm'
                  variant='plain'
                  color='neutral'
                  onClick={retryUpload}
                  startDecorator={<ArrowClockwise />}
                >
                  {intl.formatMessage({ defaultMessage: 'Retry' })}
                </Button>
              </div>
            ) : (
              <div className='flex-1 min-w-0'>
                <Tooltip title={file?.name}>
                  <Typography level='title-sm' className='truncate'>
                    {file.name}
                  </Typography>
                </Tooltip>
                <Typography level='body-sm' sx={{ textTransform: 'uppercase' }}>
                  {file?.type}
                </Typography>
                {status === 'loading' && <LinearProgress />}
              </div>
            )}
            <div className='flex items-start self-stretch'>
              <Tooltip
                title={intl.formatMessage({ defaultMessage: 'Remove file' })}
              >
                <IconButton
                  size='sm'
                  onClick={() => removePendingFile(key)}
                  disabled={status === 'loading'}
                  sx={{
                    '--IconButton-size': (theme) => theme.spacing(3),
                  }}
                >
                  <X />
                </IconButton>
              </Tooltip>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default PendingAttachments;
